import {
  LoanEditorActionsEnum,
  mersKindOptionsByActionType,
  MersPreviewOperation,
  mersPreviewOperationOptions,
  mersPreviewTargetByOperation,
  MersRegistrationKind,
  MersTransferKind,
} from 'constants/loanActionEditor';
import { getConditionalObject } from 'utils/data';

import { LoanEditorItemsKeyByType } from './LoanActionsGroup.utils';

// The MERS loan actions: one editor row each, under the action's LoanEditorItemsKeyByType form key.
export const MERS_ACTION_TYPES = Object.keys(mersKindOptionsByActionType);

// A reversal names the deactivation it reverses as Reverses; the others name their own kind.
const getKindApiField = type =>
  type === LoanEditorActionsEnum.REVERSE_MERS_DEACTIVATION ? 'reverses' : 'kind';

// Only a servicing transfer chooses flow vs seasoned; every other transfer is sent as flow.
const isFlowTransfer = item =>
  item.kind?.value === MersTransferKind.SERVICING_RIGHTS ? !!item.flow : true;

export const getMersActionApiFormat = (formItem, common) => {
  const item = formItem[LoanEditorItemsKeyByType[common.type]][0];

  return {
    ...common,
    [getKindApiField(common.type)]: item.kind?.value,
    failureTaskTypeId: item.failureTaskType?.taskTypeId ?? null,
    ...getConditionalObject(common.type === LoanEditorActionsEnum.REGISTER_WITH_MERS, () => ({
      // MERS accepts a pre-closing registration only for an iRegistration.
      preClosing: item.kind?.value === MersRegistrationKind.I_REGISTRATION && !!item.preClosing,
    })),
    ...getConditionalObject(common.type === LoanEditorActionsEnum.TRANSFER_WITH_MERS, () => ({
      flow: isFlowTransfer(item),
    })),
  };
};

export const getMersActionFormFormat = (
  actionItem,
  itemType,
  timedSequenceOptions,
  { taskTypes = {} } = {},
) => {
  const { type } = actionItem;
  const kind = actionItem[getKindApiField(type)];

  return {
    itemType,
    timedSequenceOptions,
    [LoanEditorItemsKeyByType[type]]: [
      {
        kind: mersKindOptionsByActionType[type].find(({ value }) => value === kind) ?? null,
        ...getConditionalObject(type === LoanEditorActionsEnum.REGISTER_WITH_MERS, () => ({
          preClosing: !!actionItem.preClosing,
        })),
        ...getConditionalObject(type === LoanEditorActionsEnum.TRANSFER_WITH_MERS, () => ({
          flow: actionItem.flow !== false,
        })),
        failureTaskType: taskTypes[actionItem.failureTaskTypeId] ?? null,
      },
    ],
  };
};

// A preview chooses from the kinds of the MERS action it previews.
const findPreviewKindOption = (operation, kind) => {
  const target = mersPreviewTargetByOperation[operation];

  return target
    ? (mersKindOptionsByActionType[target.actionType].find(({ value }) => value === kind) ?? null)
    : null;
};

// The editor keeps one kind; the API carries it in the field of the previewed action's kind (the others are null), with
// pre-closing and flow sent under the same rules as Register with MERS and Transfer with MERS.
export const getMersPreviewApiFormat = (formItem, common) => {
  const item = formItem[LoanEditorItemsKeyByType[common.type]][0];
  const operation = item.operation?.value ?? null;
  const kind = item.kind?.value ?? null;

  return {
    ...common,
    operation,
    ...Object.fromEntries(
      Object.entries(mersPreviewTargetByOperation).map(([targetOperation, { kindField }]) => [
        kindField,
        targetOperation === operation ? kind : null,
      ]),
    ),
    preClosing:
      operation === MersPreviewOperation.REGISTRATION &&
      kind === MersRegistrationKind.I_REGISTRATION &&
      !!item.preClosing,
    flow: operation === MersPreviewOperation.TRANSFER && isFlowTransfer(item),
  };
};

export const getMersPreviewFormFormat = (actionItem, itemType, timedSequenceOptions) => {
  const { type, operation } = actionItem;
  const kindField = mersPreviewTargetByOperation[operation]?.kindField;

  return {
    itemType,
    timedSequenceOptions,
    [LoanEditorItemsKeyByType[type]]: [
      {
        operation: mersPreviewOperationOptions.find(({ value }) => value === operation) ?? null,
        kind: kindField ? findPreviewKindOption(operation, actionItem[kindField]) : null,
        preClosing: !!actionItem.preClosing,
        // The editor's default for a transfer is flow, as for Transfer with MERS.
        flow: operation === MersPreviewOperation.TRANSFER ? actionItem.flow !== false : true,
      },
    ],
  };
};
